/**
 * Credentials screen conditional logic (MISC Onboarding Sync, Sept 21):
 * - Only states selected on the entity's States screen are shown.
 * - Per state: "Are you currently writing business in this state?"
 *     Yes → login required.
 *     No  → check the zero-report rule. Zero-report login state (e.g. Kentucky) → login still required;
 *           otherwise (e.g. Florida) → greyed out, no login requested.
 * - Systems that file per individual (sheet "Filings made under" = INDIVIDUAL) get one credential row per
 *   licensed individual, with the individual's name attached, and unlimited rows can be added.
 */
import type {
  BusinessEntity,
  CredentialRecord,
  CredentialRecordType,
  WritingBusinessAnswer,
} from '../types/onboarding'
import {
  type StatePortalRule,
  getPortalRule,
  platformCoverage,
  platformRulesForStates,
  portalRulesForState,
} from '../data/stateLoginRules'
import { newId } from './ids'

export type PortalRequirement = 'required' | 'optional' | 'notNeeded'

/** null = the writing-business question has not been answered yet. */
export function statePortalRequirement(
  rule: StatePortalRule,
  answer: WritingBusinessAnswer | undefined,
): PortalRequirement | null {
  if (!answer) return null
  if (answer === 'yes') return 'required'
  if (rule.zeroReportLogin === 'yes') return 'required'
  if (rule.zeroReportLogin === 'optional') return 'optional'
  return 'notNeeded'
}

/** States a multi-state platform covers, within the entity's selected states. */
export function platformCoveredStates(rule: StatePortalRule, statesToHandle: readonly string[]): string[] {
  return platformCoverage(rule.id, statesToHandle)
}

export function platformRequirement(entity: BusinessEntity, rule: StatePortalRule): PortalRequirement | null {
  const answers = platformCoveredStates(rule, entity.statesToHandle)
    .map((s) => entity.writingBusiness[s])
    .filter((a): a is WritingBusinessAnswer => Boolean(a))
  if (answers.length === 0) return null
  if (answers.includes('yes')) return 'required'
  if (rule.zeroReportLogin === 'yes') return 'required'
  if (rule.zeroReportLogin === 'optional') return 'optional'
  return 'notNeeded'
}

export interface PortalWithRequirement {
  rule: StatePortalRule
  requirement: PortalRequirement | null
}

/** Every portal that applies to the entity's selected states, with its current requirement. */
export function entityPortals(entity: BusinessEntity): PortalWithRequirement[] {
  const statePortals = entity.statesToHandle.flatMap((code) =>
    portalRulesForState(code).map((rule) => ({
      rule,
      requirement: statePortalRequirement(rule, entity.writingBusiness[code]),
    })),
  )
  const platforms = platformRulesForStates(entity.statesToHandle).map((rule) => ({
    rule,
    requirement: platformRequirement(entity, rule),
  }))
  return [...statePortals, ...platforms]
}

export function portalNeedsLogin(requirement: PortalRequirement | null): boolean {
  return requirement === 'required' || requirement === 'optional'
}

function emptyRecord(portalId: string, recordType: CredentialRecordType, personId: string | null): CredentialRecord {
  return { id: newId('CRED'), portalId, recordType, personId, username: '', password: '', securityQuestions: '' }
}

/** People whose logins a per-individual system needs: DRLPs assigned to the state, plus individual licensees. */
export function peopleForPortal(entity: BusinessEntity, rule: StatePortalRule) {
  const assignedDrlps = rule.stateCode
    ? entity.drlps.filter((d) => d.assignedStates.includes(rule.stateCode as string))
    : entity.drlps
  const drlps = assignedDrlps.length > 0 ? assignedDrlps : entity.drlps
  return [
    ...drlps.map((d) => ({ recordType: 'drlp' as const, personId: d.id })),
    ...entity.individuals.map((i) => ({ recordType: 'individual' as const, personId: i.id })),
  ]
}

export function defaultCredentialRows(entity: BusinessEntity, rule: StatePortalRule): CredentialRecord[] {
  if (rule.filingsMadeUnder === 'INDIVIDUAL') {
    const people = peopleForPortal(entity, rule)
    if (people.length === 0) return [emptyRecord(rule.id, 'individual', null)]
    return people.map((p) => emptyRecord(rule.id, p.recordType, p.personId))
  }
  return [emptyRecord(rule.id, 'entity', null)]
}

/** Rows for people on this portal who don't have one yet ("Add a login for each individual"). */
export function missingPersonRows(entity: BusinessEntity, rule: StatePortalRule): CredentialRecord[] {
  const existing = new Set(
    entity.credentials.filter((c) => c.portalId === rule.id && c.personId).map((c) => c.personId),
  )
  return peopleForPortal(entity, rule)
    .filter((p) => !existing.has(p.personId))
    .map((p) => emptyRecord(rule.id, p.recordType, p.personId))
}

export function newCredentialRow(portalId: string, recordType: CredentialRecordType, personId: string | null) {
  return emptyRecord(portalId, recordType, personId)
}

/**
 * Keep credential rows consistent with the entity's states and answers:
 * - drop rows (and seed markers) for portals that no longer apply to any selected state;
 * - create default rows the first time a portal needs a login.
 */
export function syncEntityCredentials(entity: BusinessEntity): BusinessEntity {
  const portals = entityPortals(entity)
  const applicable = new Set(portals.map((p) => p.rule.id))
  let credentials = entity.credentials.filter((c) => applicable.has(c.portalId))
  let seeded = entity.seededPortalIds.filter((id) => applicable.has(id))

  for (const { rule, requirement } of portals) {
    if (portalNeedsLogin(requirement) && !seeded.includes(rule.id)) {
      credentials = [...credentials, ...defaultCredentialRows(entity, rule)]
      seeded = [...seeded, rule.id]
    }
  }
  return { ...entity, credentials, seededPortalIds: seeded }
}

export function credentialPersonName(entity: BusinessEntity, record: CredentialRecord): string {
  if (record.recordType === 'entity') return entity.name || 'Business entity'
  if (!record.personId) return ''
  const person =
    record.recordType === 'drlp'
      ? entity.drlps.find((d) => d.id === record.personId)
      : entity.individuals.find((i) => i.id === record.personId)
  return person?.fullName ?? ''
}

export function isCredentialRowComplete(record: CredentialRecord): boolean {
  const hasOwner = record.recordType === 'entity' || Boolean(record.personId)
  return hasOwner && record.username.trim().length > 0 && record.password.length > 0
}

export function credentialRowsForPortal(entity: BusinessEntity, portalId: string): CredentialRecord[] {
  return entity.credentials.filter((c) => c.portalId === portalId)
}

export function portalLabelForRecord(record: CredentialRecord): string {
  const rule = getPortalRule(record.portalId)
  if (!rule) return record.portalId
  return rule.stateCode ? `${rule.stateLabel} – ${rule.system}` : rule.stateLabel
}
